import { useNavigate } from '@tanstack/react-router';
import { Route } from '@/routes/_authenticated/setup/providers';
import type { ColumnDef } from '@tanstack/react-table';
import type { Path } from 'react-hook-form';
import type {
  CreateProviderRequest,
  ProviderResponse,
  ProviderLocationWithDetails,
  UpdateProviderRequest,
} from '@vagusx/clients';
import {
  HttpError,
  PROVIDER_ROLES,
  expandSpokenName,
  sameSpokenLine,
  spokenNameIssue,
  suggestSpokenName,
} from '@vagusx/clients';
import { P } from '@vagusx/shared';
import { MapPin, Pencil, RotateCcw, UserMinus, Plus, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@/lib/zod-resolver';
import { useFollowedSuggestion } from '@/hooks/use-followed-suggestion';
import { SuggestedBadge, SpokenValue } from '@/components/custom/suggested-badge';
import { PlaySpokenButton } from '@/components/custom/play-spoken-button';
import { z } from 'zod';
import { toast } from 'sonner';
import {
  useProviders,
  useInactiveProviders,
  useProviderLocations,
  useProvider,
  useCreateProvider,
  useUpdateProvider,
  useDeleteProvider,
  useReactivateProvider,
  useAssignProviderLocation,
  useRemoveProviderLocation,
  useSetPrimaryProviderLocation,
} from '@/hooks/use-providers';
import { useHasPermission } from '@/hooks/use-permissions';
import { LocationPicker } from '@/components/custom/location-picker';
import { SearchableListSection } from '@/components/custom/searchable-list-section';
import { DataTable, SortableHeader } from '@/components/data-table';
import { TablePage } from '@/components/table-page';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { SaveButton } from '@/components/custom/save-button';
import { DetailRow, DetailSection } from '@/components/custom/detail-section';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import {
  SheetActions,
  SheetBody,
  SheetCancel,
  SheetLoading,
} from '@/components/custom/sheet-layout';
import { useDiscardGuard } from '@/hooks/use-discard-guard';
import { cn } from '@/lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsContent, TabsTrigger } from '@/components/ui/tabs';
import { PageTabsList } from '@/components/custom/page-tabs-list';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

// =============================================================================
// Form schema
// =============================================================================

// Empty string is allowed for optional fields and converted to null on submit.
const providerFormSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email format'),
  phone: z.string().optional(),
  role: z.string().optional(),
  specialty: z.string().optional(),
  npi: z.string().optional(),
  gender: z.string().optional(),
  // Kept as a string in the form so an emptied box round-trips as "clear this"
  // rather than NaN. Range is deliberately unconstrained — see ADR-141.
  weight: z
    .string()
    .optional()
    .refine((v) => !v || !Number.isNaN(Number(v)), 'Weight must be a number'),
  ehrProviderId: z.string().optional(),
  pmProviderId: z.string().optional(),
  // The spoken name (ADR-342): the one line the voice agent says. Same rule
  // the API enforces, after the same expansion, so the practice sees what
  // has no spoken form here rather than as a failed save.
  spokenName: z.string().superRefine((v, ctx) => {
    const issue = spokenNameIssue(expandSpokenName(v));
    if (issue) ctx.addIssue({ code: 'custom', message: issue });
  }),
});

type ProviderFormValues = z.infer<typeof providerFormSchema>;

/**
 * Placeholder for the provider-selection fields when they have no value yet.
 *
 * `DetailRow` hides an empty value, which is right for descriptive fields like
 * Specialty or NPI — absent data, nothing to say. Weight is different: it is a
 * knob the practice is expected to SET, and it feeds the referral ranking.
 * Hiding it makes an unconfigured field indistinguishable from a field that
 * doesn't exist, so nobody ever goes and fills it in.
 */
const NOT_SET = 'Not set';

/** One decimal, so 4 and 4.5 read consistently in the detail panes. */
/** What the agent is told it was built from, for the Suggested badge (ADR-342). */
const SPOKEN_NAME_FROM = 'the name and role';

/** The name the voice agent says, badged Suggested when it follows the record (ADR-342). */
function formatSpokenName(provider: ProviderResponse): React.ReactNode {
  // Null, not an empty element, so `DetailRow` hides the row.
  if (!provider.spokenName) return null;
  // Suggested by the same test the API stores null on, so a stored line equal to
  // the suggestion reads the same here as in the editor.
  const suggested =
    !provider.customSpokenName ||
    sameSpokenLine(provider.customSpokenName, suggestSpokenName(provider));
  return (
    <SpokenValue
      value={provider.spokenName}
      suggested={suggested}
      from={SPOKEN_NAME_FROM}
      label="spoken name"
    />
  );
}

function formatWeight(weight: number | null | undefined): string | null {
  return weight === null || weight === undefined ? null : weight.toFixed(1);
}

// =============================================================================
// Table columns
// =============================================================================

function buildColumns(opts: {
  canEdit: boolean;
  onEdit: (id: string) => void;
}): ColumnDef<ProviderResponse>[] {
  const cols: ColumnDef<ProviderResponse>[] = [
    {
      accessorKey: 'name',
      accessorFn: (row) => `${row.firstName} ${row.lastName}`,
      header: ({ column }) => <SortableHeader column={column}>Name</SortableHeader>,
      cell: ({ row }) => (
        <span className="font-medium">
          {row.original.firstName} {row.original.lastName}
        </span>
      ),
      meta: { headerClassName: 'w-[30%]', cellClassName: 'truncate' },
    },
    {
      accessorKey: 'role',
      header: 'Role',
      cell: ({ row }) =>
        row.original.role ? (
          <Badge variant="outline">{row.original.role}</Badge>
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
      meta: { headerClassName: 'w-[20%]' },
    },
    {
      accessorKey: 'specialty',
      header: 'Specialty',
      cell: ({ row }) =>
        row.original.specialty ? (
          <Badge variant="secondary">{row.original.specialty}</Badge>
        ) : (
          <span className="text-muted-foreground">-</span>
        ),
      meta: { headerClassName: 'w-[25%]' },
    },
    {
      accessorKey: 'npi',
      header: 'NPI',
      cell: ({ row }) => row.original.npi || <span className="text-muted-foreground">-</span>,
      meta: { headerClassName: 'w-[18%]', cellClassName: 'truncate' },
    },
  ];

  if (opts.canEdit) {
    cols.push({
      id: 'actions',
      header: '',
      meta: { headerClassName: 'w-[64px]', cellClassName: 'text-right' },
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="icon"
          // Hidden until the row is hovered; `focus-visible` keeps it reachable
          // by keyboard. Tailwind gates `group-hover` behind `@media (hover:hover)`,
          // so anything with a touchscreen keeps it permanently visible — including
          // a hybrid laptop, whose *primary* input hovers even when the finger the
          // user actually taps with does not. Without that the button would be
          // invisible but still tappable, swallowing the row tap. `any-pointer`
          // (not `hover`) is the test, because it considers every attached input.
          // No `transition-opacity` here: Button's base `transition-all` already
          // covers opacity, and tailwind-merge would drop it for a narrower class.
          className="h-7 w-7 opacity-0 focus-visible:opacity-100 group-hover:opacity-100 [@media(any-pointer:coarse)]:opacity-100"
          aria-label={`Edit ${row.original.firstName} ${row.original.lastName}`}
          onClick={(e) => {
            e.stopPropagation();
            opts.onEdit(row.original.id);
          }}
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      ),
    });
  }

  return cols;
}

const locationSearchFields = ({
  location,
}: ProviderLocationWithDetails): Array<string | null | undefined> => [
  location.name,
  location.address,
  location.city,
  location.state,
  location.zip,
];

/** `?id=` value that opens the create sheet instead of an existing provider. */
const NEW_PROVIDER_ID = 'new';

// Module-level — never changes, no need to rebuild per render.
const inactiveColumns = buildColumns({ canEdit: false, onEdit: () => undefined });

// =============================================================================
// Page
// =============================================================================

export default function ProvidersPage(): React.ReactElement {
  const searchParams = Route.useSearch();
  const navigate = useNavigate();
  const canEdit = useHasPermission(P.PROVIDERS_EDIT);

  const selectedProviderId = searchParams.id ?? null;
  // `?id=new` opens the create sheet — same sentinel the Locations page uses.
  const isCreating = selectedProviderId === NEW_PROVIDER_ID;
  const selectedInactiveId = searchParams.inactiveId ?? null;
  // Tab is URL-driven so deep-links land on the right list. Defaults to active
  // unless an inactive sheet is open or `?tab=inactive` is set.
  const tab: 'active' | 'inactive' =
    searchParams.tab ?? (selectedInactiveId ? 'inactive' : 'active');

  const { data, isLoading, error } = useProviders();
  // Eager: the inactive count is always shown in the tab label, so we always
  // need this list. (Cheap query — typically <100 rows; capped at LIST_LIMIT.)
  const { data: inactiveData, isLoading: inactiveLoading } = useInactiveProviders();

  const [isEditing, setIsEditing] = useState(false);

  const providers: ProviderResponse[] = data?.items ?? [];
  const inactiveProviders: ProviderResponse[] = inactiveData?.items ?? [];

  const handleProviderSelect = useCallback(
    (provider: ProviderResponse) => {
      setIsEditing(false);
      navigate({ to: '/setup/providers', search: { id: provider.id }, replace: true });
    },
    [navigate]
  );

  const handleEditFromRow = useCallback(
    (id: string) => {
      setIsEditing(true);
      navigate({ to: '/setup/providers', search: { id }, replace: true });
    },
    [navigate]
  );

  const handleCloseSheet = useCallback(() => {
    setIsEditing(false);
    navigate({ to: '/setup/providers', search: { tab }, replace: true });
  }, [navigate, tab]);

  const handleCreate = useCallback(() => {
    navigate({ to: '/setup/providers', search: { id: NEW_PROVIDER_ID }, replace: true });
  }, [navigate]);

  // A new provider has no locations yet, and without one it can't be scheduled
  // — so land on its detail sheet, where the Locations "Add" control lives.
  const handleCreated = useCallback(
    (provider: ProviderResponse) => {
      setIsEditing(false);
      navigate({ to: '/setup/providers', search: { id: provider.id }, replace: true });
    },
    [navigate]
  );

  const handleTabChange = useCallback(
    (next: string) => {
      const nextTab = next === 'inactive' ? 'inactive' : 'active';
      navigate({
        to: '/setup/providers',
        search: { tab: nextTab },
        replace: true,
      });
    },
    [navigate]
  );

  const handleInactiveSelect = useCallback(
    (provider: ProviderResponse) => {
      navigate({
        to: '/setup/providers',
        search: { inactiveId: provider.id, tab: 'inactive' },
        replace: true,
      });
    },
    [navigate]
  );

  const handleCloseInactiveSheet = useCallback(() => {
    navigate({ to: '/setup/providers', search: { tab: 'inactive' }, replace: true });
  }, [navigate]);

  const activeColumns = useMemo(
    () => buildColumns({ canEdit, onEdit: handleEditFromRow }),
    [canEdit, handleEditFromRow]
  );

  // Inactive provider lookup — defensive against the row vanishing between
  // renders (e.g. another admin reactivates the same provider). Falls back to
  // null which closes the sheet rather than crashing.
  const selectedInactiveProvider = selectedInactiveId
    ? (inactiveProviders.find((p) => p.id === selectedInactiveId) ?? null)
    : null;

  return (
    <TablePage
      title="Providers"
      description="Manage healthcare providers in your practice"
      total={providers.length || undefined}
      isLoading={isLoading}
      headerAction={
        canEdit ? (
          <Button size="sm" onClick={handleCreate}>
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Add Provider
          </Button>
        ) : undefined
      }
    >
      <Tabs value={tab} onValueChange={handleTabChange} className="flex min-h-0 flex-1 flex-col">
        <PageTabsList className="shrink-0">
          <TabsTrigger value="active">Active ({providers.length})</TabsTrigger>
          <TabsTrigger value="inactive">Inactive ({inactiveProviders.length})</TabsTrigger>
        </PageTabsList>

        <TabsContent value="active" className="mt-4 flex min-h-0 flex-1 flex-col">
          {error ? (
            <div className="rounded-md bg-destructive/10 p-4 text-destructive">
              Failed to load providers. Please try again.
            </div>
          ) : (
            <DataTable
              columns={activeColumns}
              data={providers}
              isLoading={isLoading}
              searchColumn="name"
              searchPlaceholder="Search providers..."
              onRowClick={handleProviderSelect}
              emptyMessage="No providers found."
              fillHeight
              tableClassName="table-fixed"
            />
          )}
        </TabsContent>

        <TabsContent value="inactive" className="mt-4 flex min-h-0 flex-1 flex-col">
          <DataTable
            columns={inactiveColumns}
            data={inactiveProviders}
            isLoading={inactiveLoading}
            searchColumn="name"
            searchPlaceholder="Search inactive providers..."
            onRowClick={handleInactiveSelect}
            emptyMessage="No inactive providers."
            fillHeight
            tableClassName="table-fixed"
          />
        </TabsContent>
      </Tabs>

      {isCreating && canEdit && (
        <ProviderEditorSheet onClose={handleCloseSheet} onCreated={handleCreated} />
      )}

      {selectedProviderId && !isCreating && (
        <ProviderDetailSheet
          providerId={selectedProviderId}
          isEditing={isEditing}
          canEdit={canEdit}
          onEdit={() => setIsEditing(true)}
          onClose={handleCloseSheet}
          onEditDone={() => setIsEditing(false)}
        />
      )}

      {selectedInactiveProvider && (
        <InactiveProviderSheet
          provider={selectedInactiveProvider}
          canEdit={canEdit}
          onClose={handleCloseInactiveSheet}
        />
      )}
    </TablePage>
  );
}

// =============================================================================
// Inactive Provider Sheet (read-only + Reactivate)
// =============================================================================

function InactiveProviderSheet({
  provider,
  canEdit,
  onClose,
}: {
  provider: ProviderResponse;
  canEdit: boolean;
  onClose: () => void;
}): React.ReactElement {
  const reactivate = useReactivateProvider();
  const [confirmReactivate, setConfirmReactivate] = useState(false);
  // Locations don't cascade-delete on soft-delete, so an inactive provider
  // may still have assignments — show them so operators see what'll come
  // back online on reactivate.
  const { data: providerLocations, isLoading: locationsLoading } = useProviderLocations(
    provider.id
  );
  const locations = providerLocations ?? [];

  const handleReactivate = async () => {
    try {
      await reactivate.mutateAsync(provider.id);
      toast.success('Provider reactivated');
      onClose();
    } catch {
      // global mutation error handler shows the toast
    }
  };

  return (
    <Sheet open={true} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-full max-w-lg sm:max-w-xl">
        <SheetHeader className="px-6">
          <SheetTitle>
            {provider.firstName} {provider.lastName}
          </SheetTitle>
          <SheetDescription>Inactive — provider was deactivated</SheetDescription>
        </SheetHeader>

        <SheetBody className="px-6 pb-6 space-y-2">
          <DetailSection title="Contact Information">
            <DetailRow label="Email" value={provider.email} />
            <DetailRow label="Phone" value={provider.phone} />
          </DetailSection>

          <DetailSection title="Professional Info">
            <DetailRow label="Role" value={provider.role} />
            <DetailRow label="Specialty" value={provider.specialty} />
            <DetailRow label="Gender" value={provider.gender} />
            <DetailRow label="NPI" value={provider.npi} />
            <DetailRow label="Spoken Name" value={formatSpokenName(provider)} />
          </DetailSection>

          <DetailSection title="External References">
            <DetailRow label="EHR Provider ID" value={provider.ehrProviderId} />
            <DetailRow label="PM Provider ID" value={provider.pmProviderId} />
          </DetailSection>

          <SearchableListSection
            key={provider.id}
            title="Locations"
            items={locations}
            isLoading={locationsLoading}
            getKey={(pl) => pl.id}
            searchFields={locationSearchFields}
            searchPlaceholder="Search by name or address..."
            loadingLabel="Loading locations..."
            emptyLabel="No locations assigned"
            noMatchLabel="No locations match your search"
            renderItem={(pl) => <LocationCard providerLocation={pl} onClick={() => undefined} />}
          />

          <DetailSection title="Provider Selection">
            <DetailRow label="Weight" value={formatWeight(provider.weight) ?? NOT_SET} />
          </DetailSection>
        </SheetBody>

        {canEdit && (
          <SheetActions className="px-6">
            <SheetCancel />
            <Button onClick={() => setConfirmReactivate(true)} disabled={reactivate.isPending}>
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
              Reactivate
            </Button>
          </SheetActions>
        )}
      </SheetContent>

      <AlertDialog open={confirmReactivate} onOpenChange={setConfirmReactivate}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reactivate provider?</AlertDialogTitle>
            <AlertDialogDescription>
              {provider.firstName} {provider.lastName} will become assignable again and reappear in
              the active providers list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reactivate.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleReactivate} disabled={reactivate.isPending}>
              Reactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  );
}

// =============================================================================
// Provider Detail Sheet (View + Edit toggle)
// =============================================================================

function ProviderDetailSheet({
  providerId,
  isEditing,
  canEdit,
  onEdit,
  onClose,
  onEditDone,
}: {
  providerId: string;
  isEditing: boolean;
  canEdit: boolean;
  onEdit: () => void;
  onClose: () => void;
  onEditDone: () => void;
}): React.ReactElement {
  const { data: provider } = useProvider(providerId);
  const { data: providerLocations, isLoading: locationsLoading } = useProviderLocations(providerId);
  const navigate = useNavigate();

  const assignLocation = useAssignProviderLocation();
  const removeLocation = useRemoveProviderLocation();
  const setPrimaryLocation = useSetPrimaryProviderLocation();
  // Any association mutation in flight disables every association control in the
  // sheet, so a second click can't fire a duplicate request (which would 404/409).
  const isMutating =
    assignLocation.isPending || removeLocation.isPending || setPrimaryLocation.isPending;

  const locations = providerLocations ?? [];
  const assignedLocationIds = useMemo(() => locations.map((pl) => pl.locationId), [locations]);

  const handleAddLocation = async (locationId: string): Promise<void> => {
    if (!locationId) return;
    try {
      // Primary is enforced server-side (the provider's first location wins), so
      // the client doesn't compute it from a possibly-stale cached count.
      await assignLocation.mutateAsync({ providerId, data: { locationId } });
      toast.success('Location assigned');
    } catch {
      // global mutation error handler shows the toast
    }
  };

  const handleRemoveLocation = async (locationId: string): Promise<void> => {
    try {
      await removeLocation.mutateAsync({ providerId, locationId });
      toast.success('Location removed');
    } catch {
      // global mutation error handler shows the toast
    }
  };

  const handleSetPrimaryLocation = async (locationId: string): Promise<void> => {
    try {
      await setPrimaryLocation.mutateAsync({ providerId, locationId });
      toast.success('Primary location updated');
    } catch {
      // global mutation error handler shows the toast
    }
  };

  // View and edit share this one sheet, so entering edit swaps its content
  // instead of sliding a second sheet in, and an edit opened from the table
  // shows a loading state — never the view — until the provider arrives.
  const discard = useDiscardGuard();
  const closeSheet = (): void => {
    onEditDone();
    onClose();
  };
  const handleOpenChange = (open: boolean): void => {
    if (open) return;
    if (isEditing) discard.guard(closeSheet);
    else closeSheet();
  };

  return (
    <Sheet open={true} onOpenChange={handleOpenChange}>
      <SheetContent className={cn(PROVIDER_SHEET_CLASS, isEditing && 'gap-0')}>
        {isEditing ? (
          provider ? (
            <ProviderEditor
              provider={provider}
              onClose={() => discard.guard(closeSheet)}
              onSaved={onEditDone}
              onDirtyChange={discard.setDirty}
            />
          ) : (
            <SheetLoading title="Edit Provider" className="px-6" />
          )
        ) : (
          <>
            <SheetHeader className="px-6">
              <SheetTitle>
                {provider?.firstName} {provider?.lastName}
              </SheetTitle>
              <SheetDescription>Provider details</SheetDescription>
            </SheetHeader>

            {provider && (
              <SheetBody className="px-6 pb-6 space-y-2">
                <DetailSection title="Contact Information">
                  <DetailRow label="Email" value={provider.email} />
                  <DetailRow label="Phone" value={provider.phone} />
                </DetailSection>

                <DetailSection title="Professional Info">
                  <DetailRow label="Role" value={provider.role} />
                  <DetailRow label="Specialty" value={provider.specialty} />
                  <DetailRow label="Gender" value={provider.gender} />
                  <DetailRow label="NPI" value={provider.npi} />
                  <DetailRow label="Spoken Name" value={formatSpokenName(provider)} />
                </DetailSection>

                <DetailSection title="External References">
                  <DetailRow label="EHR Provider ID" value={provider.ehrProviderId} />
                  <DetailRow label="PM Provider ID" value={provider.pmProviderId} />
                </DetailSection>

                <SearchableListSection
                  key={providerId}
                  title="Locations"
                  items={locations}
                  isLoading={locationsLoading}
                  getKey={(pl) => pl.id}
                  searchFields={locationSearchFields}
                  searchPlaceholder="Search by name or address..."
                  loadingLabel="Loading locations..."
                  emptyLabel="No locations assigned"
                  noMatchLabel="No locations match your search"
                  action={
                    canEdit ? (
                      <LocationPicker
                        selected=""
                        onChange={handleAddLocation}
                        excludeIds={assignedLocationIds}
                        disabled={isMutating}
                        trigger={
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 gap-1 px-2 text-xs text-muted-foreground"
                            disabled={isMutating}
                          >
                            <Plus className="h-3.5 w-3.5" />
                            Add
                          </Button>
                        }
                      />
                    ) : undefined
                  }
                  renderItem={(pl) => (
                    <LocationCard
                      providerLocation={pl}
                      onClick={() =>
                        navigate({ to: '/setup/locations', search: { id: pl.locationId } })
                      }
                      canEdit={canEdit}
                      disabled={isMutating}
                      onSetPrimary={() => handleSetPrimaryLocation(pl.locationId)}
                      onRemove={() => handleRemoveLocation(pl.locationId)}
                    />
                  )}
                />

                {/* Kept last and in its own section because these two are POLICY,
                not provider identity: they tune how referrals are ranked ACROSS
                clinicians rather than describing this one. Rendered even when
                unset — a hidden row makes "not configured" look identical to
                "field doesn't exist", and nobody goes and fills those in. */}
                <DetailSection title="Provider Selection">
                  <DetailRow label="Weight" value={formatWeight(provider.weight) ?? NOT_SET} />
                </DetailSection>
              </SheetBody>
            )}

            {canEdit && (
              <SheetActions className="px-6">
                <SheetCancel />
                <Button onClick={onEdit}>
                  <Pencil className="mr-1.5 h-3.5 w-3.5" />
                  Edit
                </Button>
              </SheetActions>
            )}
          </>
        )}
      </SheetContent>
      {discard.dialog}
    </Sheet>
  );
}

// =============================================================================
// Provider Editor (Create / Edit)
// =============================================================================

const EMPTY_FORM_VALUES: ProviderFormValues = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  role: '',
  specialty: '',
  npi: '',
  gender: '',
  weight: '',
  ehrProviderId: '',
  pmProviderId: '',
  spokenName: '',
};

function providerToFormValues(provider: ProviderResponse | null): ProviderFormValues {
  if (!provider) return EMPTY_FORM_VALUES;
  return {
    firstName: provider.firstName,
    lastName: provider.lastName,
    email: provider.email,
    phone: provider.phone ?? '',
    role: provider.role ?? '',
    specialty: provider.specialty ?? '',
    npi: provider.npi ?? '',
    gender: provider.gender ?? '',
    weight: provider.weight?.toString() ?? '',
    ehrProviderId: provider.ehrProviderId ?? '',
    pmProviderId: provider.pmProviderId ?? '',
    spokenName: provider.spokenName,
  };
}

// Empty inputs mean different things per verb: on update an emptied box CLEARS
// the stored value (null); on create there is nothing to clear, so it's omitted.
function toUpdatePayload(values: ProviderFormValues): UpdateProviderRequest {
  return {
    firstName: values.firstName,
    lastName: values.lastName,
    email: values.email,
    phone: values.phone || null,
    role: values.role || null,
    specialty: values.specialty || null,
    npi: values.npi || null,
    gender: values.gender || null,
    weight: values.weight ? Number(values.weight) : null,
    ehrProviderId: values.ehrProviderId || null,
    pmProviderId: values.pmProviderId || null,
    // Sent as shown; the API stores null when it is the suggestion (ADR-342).
    spokenName: values.spokenName || null,
  };
}

function toCreatePayload(values: ProviderFormValues): CreateProviderRequest {
  return {
    firstName: values.firstName,
    lastName: values.lastName,
    email: values.email,
    phone: values.phone || undefined,
    role: values.role || undefined,
    specialty: values.specialty || undefined,
    npi: values.npi || undefined,
    gender: values.gender || undefined,
    weight: values.weight ? Number(values.weight) : undefined,
    ehrProviderId: values.ehrProviderId || undefined,
    pmProviderId: values.pmProviderId || undefined,
    spokenName: values.spokenName || undefined,
  };
}

const PROVIDER_SHEET_CLASS = 'w-full max-w-lg sm:max-w-xl';

/** The create sheet. Editing an existing provider happens inside its detail sheet. */
function ProviderEditorSheet({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (provider: ProviderResponse) => void;
}): React.ReactElement {
  const discard = useDiscardGuard();
  return (
    <Sheet open={true} onOpenChange={(o) => !o && discard.guard(onClose)}>
      <SheetContent className={cn(PROVIDER_SHEET_CLASS, 'gap-0')}>
        <ProviderEditor
          provider={null}
          onClose={() => discard.guard(onClose)}
          onCreated={onCreated}
          onDirtyChange={discard.setDirty}
        />
      </SheetContent>
      {discard.dialog}
    </Sheet>
  );
}

/** Header, form and footer of the provider editor — rendered inside a sheet's content. */
function ProviderEditor({
  provider,
  onClose,
  onSaved,
  onCreated,
  onDirtyChange,
}: {
  /** `null` = create a new provider. */
  provider: ProviderResponse | null;
  onClose: () => void;
  onSaved?: () => void;
  onCreated?: (provider: ProviderResponse) => void;
  /** Lets the hosting sheet guard its X, Escape and Cancel against unsaved changes. */
  onDirtyChange: (isDirty: boolean) => void;
}): React.ReactElement {
  const createMutation = useCreateProvider();
  const updateMutation = useUpdateProvider();
  const deleteMutation = useDeleteProvider();
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);

  const form = useForm<ProviderFormValues>({
    resolver: zodResolver(providerFormSchema),
    defaultValues: providerToFormValues(provider),
  });

  const { isDirty } = form.formState;

  useEffect(() => {
    form.reset(providerToFormValues(provider));
  }, [provider, form]);

  useEffect(() => {
    onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  // The hosting sheet asks before discarding unsaved changes.
  const handleClose = onClose;

  const onSubmit = async (values: ProviderFormValues): Promise<void> => {
    try {
      if (provider) {
        await updateMutation.mutateAsync({ id: provider.id, data: toUpdatePayload(values) });
        toast.success('Provider updated');
        onSaved?.();
      } else {
        const created = await createMutation.mutateAsync(toCreatePayload(values));
        toast.success('Provider added');
        onCreated?.(created);
      }
    } catch (err) {
      // Map server-side validation errors back to the matching fields so they
      // render in place via the existing <p className="text-destructive"> slots.
      // Anything else falls through to the global mutation toast.
      if (err instanceof HttpError && err.isValidation) {
        for (const fe of err.fieldErrors) {
          form.setError(fe.field as Path<ProviderFormValues>, { message: fe.message });
        }
      } else {
        throw err;
      }
    }
  };

  const handleDeactivate = async () => {
    if (!provider) return;
    try {
      await deleteMutation.mutateAsync(provider.id);
      toast.success('Provider deactivated');
      // Deactivated, so nothing left to save: close without asking.
      onDirtyChange(false);
      onSaved?.();
      onClose();
    } catch {
      // global mutation error handler shows the toast
    }
  };

  return (
    <>
      <SheetHeader className="px-6">
        <SheetTitle>{provider ? 'Edit Provider' : 'Add Provider'}</SheetTitle>
        <SheetDescription>
          {provider
            ? 'Update provider information'
            : 'Add a provider to your practice. Assign locations after saving.'}
        </SheetDescription>
      </SheetHeader>

      <form
        noValidate
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex min-h-0 flex-1 flex-col"
      >
        <SheetBody className="px-6 pb-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <FormField label="First name" error={form.formState.errors.firstName?.message}>
              <Input {...form.register('firstName')} />
            </FormField>
            <FormField label="Last name" error={form.formState.errors.lastName?.message}>
              <Input {...form.register('lastName')} />
            </FormField>
          </div>

          <SpokenNameField form={form} provider={provider} />

          <FormField label="Email" error={form.formState.errors.email?.message}>
            <Input type="email" {...form.register('email')} />
          </FormField>

          <div className="grid grid-cols-2 gap-3">
            <FormField label="Phone" error={form.formState.errors.phone?.message}>
              <Input {...form.register('phone')} placeholder="(555) 123-4567" />
            </FormField>
            <FormField label="Role" error={form.formState.errors.role?.message}>
              <RoleSelect form={form} currentValue={provider?.role ?? ''} />
            </FormField>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <FormField label="Specialty" error={form.formState.errors.specialty?.message}>
              <Input {...form.register('specialty')} placeholder="Cardiology" />
            </FormField>
            <FormField label="Gender" error={form.formState.errors.gender?.message}>
              <Input {...form.register('gender')} placeholder="male / female / other" />
            </FormField>
          </div>

          <FormField label="NPI" error={form.formState.errors.npi?.message}>
            <Input {...form.register('npi')} placeholder="10-digit NPI" />
          </FormField>

          <div className="grid grid-cols-2 gap-3">
            <FormField label="EHR Provider ID" error={form.formState.errors.ehrProviderId?.message}>
              <Input {...form.register('ehrProviderId')} placeholder="ID in EHR system" />
            </FormField>
            <FormField label="PM Provider ID" error={form.formState.errors.pmProviderId?.message}>
              <Input
                {...form.register('pmProviderId')}
                placeholder="ID in practice management system"
              />
            </FormField>
          </div>

          {/* Provider selection — kept last and behind a divider because these
                two are POLICY, not provider identity: they tune how referrals
                are ranked across clinicians rather than describing this one.
                Both optional — an unrated provider scores neutral in the ranking
                rather than being dropped, and a blank capacity falls back to
                their open-slot count. */}
          <Separator />

          <div className="space-y-1">
            <h4 className="text-sm font-medium">Provider Selection</h4>
            <p className="text-xs text-muted-foreground">
              Used to rank providers when a referral is matched. Both are optional.
            </p>
          </div>

          <FormField label="Weight" error={form.formState.errors.weight?.message}>
            <Input {...form.register('weight')} type="number" step={0.1} placeholder="1.0" />
          </FormField>
        </SheetBody>

        <SheetActions
          className="px-6"
          start={
            provider && (
              <Button
                type="button"
                variant="ghost"
                onClick={() => setConfirmDeactivate(true)}
                className="text-destructive hover:text-destructive"
              >
                <UserMinus className="mr-1.5 h-3.5 w-3.5" />
                Deactivate
              </Button>
            )
          }
        >
          <Button type="button" variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <SaveButton
            type="submit"
            isPending={updateMutation.isPending || createMutation.isPending}
            disabled={!isDirty}
          />
        </SheetActions>
      </form>

      <AlertDialog open={confirmDeactivate && !!provider} onOpenChange={setConfirmDeactivate}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate provider?</AlertDialogTitle>
            <AlertDialogDescription>
              {provider?.firstName} {provider?.lastName} will no longer appear in lists or be
              assignable to new appointments. Existing appointments are unaffected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeactivate}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Deactivate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// =============================================================================
// Shared Components
// =============================================================================

/**
 * Role dropdown — preserves legacy values from EHR sync (anything outside
 * PROVIDER_ROLES is shown as an extra option so editing doesn't strip it).
 *
 * Subscribes to just the `role` field via `useWatch` so unrelated form-state
 * changes don't re-render the Select.
 */
function RoleSelect({
  form,
  currentValue,
}: {
  form: ReturnType<typeof useForm<ProviderFormValues>>;
  currentValue: string;
}): React.ReactElement {
  const role = useWatch({ control: form.control, name: 'role' }) ?? '';
  const options =
    currentValue && !PROVIDER_ROLES.includes(currentValue as (typeof PROVIDER_ROLES)[number])
      ? [currentValue, ...PROVIDER_ROLES]
      : PROVIDER_ROLES;

  return (
    <Select value={role} onValueChange={(v) => form.setValue('role', v, { shouldDirty: true })}>
      <SelectTrigger>
        <SelectValue placeholder="Select role" />
      </SelectTrigger>
      <SelectContent>
        {options.map((r) => (
          <SelectItem key={r} value={r}>
            {r}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * The spoken name (ADR-342): one line, said exactly as written. It follows the
 * suggestion — the role's or credential's title plus the chart name, from the
 * same `suggestSpokenName` the API uses — until someone types in it, and goes
 * back to following it when emptied. Abbreviations are written out when the
 * box loses focus.
 */
function SpokenNameField({
  form,
  provider,
}: {
  form: ReturnType<typeof useForm<ProviderFormValues>>;
  provider: ProviderResponse | null;
}): React.ReactElement {
  const [firstName, lastName, role] = useWatch({
    control: form.control,
    name: ['firstName', 'lastName', 'role'],
  });
  const suggestion = suggestSpokenName({ firstName, lastName, role });
  // A new provider, or one whose line follows the chart, keeps following it.
  const { field, value, diverged, auto, follow } = useFollowedSuggestion({
    form,
    name: 'spokenName',
    suggestion,
    expand: expandSpokenName,
    initiallyFollows: !provider?.customSpokenName,
  });

  return (
    <FormField
      label={
        <span className="flex items-center gap-2">
          Spoken name
          {auto && <SuggestedBadge from={SPOKEN_NAME_FROM} />}
          {diverged && (
            <Button
              type="button"
              variant="link"
              className="ml-auto h-auto p-0 text-xs font-normal"
              onClick={follow}
            >
              Use suggested
            </Button>
          )}
        </span>
      }
      error={form.formState.errors.spokenName?.message}
      description="Exactly what the voice agent says when it names this provider, in full words."
    >
      <div className="flex items-center gap-2">
        <Input {...field} placeholder="Doctor Sahai" aria-label="Spoken name" />
        <PlaySpokenButton text={value} label="spoken name" />
      </div>
    </FormField>
  );
}

function FormField({
  label,
  error,
  description,
  children,
}: {
  label: React.ReactNode;
  error?: string;
  /** Helper text under the input. */
  description?: React.ReactNode;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      {children}
      {description && <p className="text-xs text-muted-foreground">{description}</p>}
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}

/**
 * Compact, divider-separated row for a provider's location assignment. Actions
 * (Make primary / Remove) render only when `canEdit` and stop propagation so
 * they don't trigger the row's navigate-to-location click.
 */
function LocationCard({
  providerLocation,
  onClick,
  canEdit = false,
  disabled = false,
  onSetPrimary,
  onRemove,
}: {
  providerLocation: ProviderLocationWithDetails;
  onClick: () => void;
  canEdit?: boolean;
  disabled?: boolean;
  onSetPrimary?: () => void;
  onRemove?: () => void;
}): React.ReactElement {
  const { location, isPrimary } = providerLocation;

  return (
    <div
      className="group flex cursor-pointer items-start gap-2.5 border-t py-2.5 first:border-t-0 hover:bg-accent/40"
      onClick={onClick}
    >
      <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">{location.name}</span>
          {isPrimary && (
            <Badge variant="secondary" className="shrink-0 px-1.5 py-0 text-[10px] font-normal">
              Primary
            </Badge>
          )}
        </div>
        <div className="truncate text-xs text-muted-foreground">
          {location.address}, {location.city}, {location.state} {location.zip}
        </div>
      </div>
      {canEdit && (onSetPrimary || onRemove) && (
        <div className="flex shrink-0 items-center gap-0.5" onClick={(e) => e.stopPropagation()}>
          {!isPrimary && onSetPrimary && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs text-muted-foreground"
              disabled={disabled}
              onClick={onSetPrimary}
            >
              Make primary
            </Button>
          )}
          {onRemove && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-6 w-6 text-muted-foreground hover:text-destructive"
              aria-label={`Remove ${location.name}`}
              disabled={disabled}
              onClick={onRemove}
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
